import React, { useState } from "react";
import {
  FontWeights,
  getTheme,
  IButtonStyles,
  IconButton,
  IIconProps,
  mergeStyleSets,
  Modal,
  TeachingBubble,
} from "@fluentui/react";
import * as uuid from "uuid";

import { useAppSelector } from "../../redux/hooks/hooks";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import LinkExistingDocument from "../document/LinkExistingDocument";
import { addNotification, createContentControlAndLaunchElaboration } from "../../redux/feature/cpm/cpm-slice";
import { CoObjectAdvanced, DocumentPartSource } from "../interfaces/CoObject";
import { createElaborationRequestAsync } from "../../redux/feature/object/remote-elaborations-slice";
import LoadingManager from "../../common/loading/LoadingManager";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { selectCpmUsername } from "../../redux/feature/settings/configurations-slice";
import { EditDocument } from "../document/EditDocument";
import { DocumentInsertionPoint } from "../../office/WordDocumentPart";
import { fetchDocumentPath } from "../../api/document/Documents";
import { getPathForFileLocationExplorer } from "../../utils/helpers/FileLocationPathHelper";
import { store } from "../../redux/store/store";
import { mergeStyles } from "@fluentui/react/lib/Styling";
import { LinkNonTagetikDocumentPanel } from "../document/LinkNonTagetikDocument";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { DimensionParameter } from "../../api/models/dimension-parameter";
import { getContentControlId } from "../../utils/helpers/ContentControlHelper";
import { INSERT_OBJECT_NOTIFICATION } from "../../utils/constants/notification";
import { DocumentPart, ElaborationItem, ElaborationRequest } from "../../api/models";
import { mapMetadataToAdvanced } from "../../redux/feature/cpm/map-metadata";
import { getDocumentPartMetadataFromCustomXml } from "../../utils/helpers/CustomXmlHelper";

export const createCoObjectDocumentPart = async (
  documentId: string,
  documentPart: DocumentPart,
  linkDetail: { partDocumentId: string } | { documentDriveItemId: string } | undefined,
  name: string,
  description: string,
  path: string,
  documentInsertionPoint: DocumentInsertionPoint,
  _creationType: string,
  advanced?: CoObjectAdvanced,
  username?: string,
  parameters?: DimensionParameter[],
  inheritableParameters?: DimensionParameter[],
  language?: string
) => {
  // when reference is the opened document, we add the part as last child
  if (documentId === documentInsertionPoint.referenceDocumentId) {
    documentInsertionPoint = undefined;
  }

  let existingCcId = await getContentControlId((linkDetail as any)?.partDocumentId);
  if (existingCcId) {
    addNotification(
      INSERT_OBJECT_NOTIFICATION,
      formatMessage("cof.createnewdocument.message.failed.title"),
      formatMessage("cof.createnewdocument.message.morethanonedocumentpart.failed"),
      { type: "warning", isTemporary: false }
    );
    return null;
  }

  let documentPartObject;
  try {
    documentPartObject = await createContentControlAndLaunchElaboration({
      userId: username,
      documentPart: documentPart,
      coObject: {
        documentId: documentId,
        info: { objectName: name, objectType: "DOCUMENT_PART_INCLUDE", info: description },
        source: { includedDocumentPartId: (linkDetail as any)?.partDocumentId, path: path } as DocumentPartSource,
        parameters: parameters,
        advanced: advanced,
      },
      language: language,
      documentInsertionPoint: documentInsertionPoint,
      inheritableParameters: inheritableParameters,
      linkDetail,
    });
  } catch (error) {
    // The caller (cpm-slice) is responsible for notifying the user and cleaning up on failure.
    console.warn("Failed to create content control and launch elaboration for document part", error);
    return null;
  }
  if (!documentPartObject) {
    // Legacy failure contract: some callers may still resolve to undefined instead of rethrowing.
    return null;
  }
  if (linkDetail) {
    const clientID = store.getState().configurationState.clientId;
    const correlationID = uuid.v4();
    let elaborationItem: ElaborationItem = {
      correlationId: correlationID,
      contentControlId: documentPartObject.contentControlId,
      dbId: null,
      formId: null,
      sheet: null,
      area: null,
      objectId: name,
      objectType: "DOCUMENT_PART_INCLUDE",
      documentPartIncludedId: documentPartObject.source.includedDocumentPartId, // TODO: check model
      elaborationType: "REFRESH",
      updateOptions: { depthLevel: "SHALLOWEST" },
      // TODO INTERNAL_REF_URL is ok for ROOT, needs to be evaluated for other type of headerAndFooter if use REF_URL
      elabResultType: "REF_URL",
    };
    let elaborationRequest: ElaborationRequest = {
      correlationId: correlationID,
      clientId: clientID,
      documentId: documentId,
      userId: username,
      tgkElaborationItemList: [elaborationItem],
    };
    await createElaborationRequestAsync(elaborationRequest);
  }
  return documentPartObject;
};

const theme = getTheme();
const contentStyles = mergeStyleSets({
  header: [
    theme.fonts.xLarge,
    {
      flex: "1 1 auto",
      borderTop: `4px solid ${theme.palette.themePrimary}`,
      color: theme.palette.neutralPrimary,
      display: "flex",
      alignItems: "center",
      fontWeight: FontWeights.semibold,
      padding: "12px 12px 14px 0px",
    },
  ],
  body: {
    flex: "4 4 auto",
    padding: "0 24px 24px 24px",
    overflowY: "hidden",
    selectors: {
      p: { margin: "14px 0" },
      "p:first-child": { marginTop: 0 },
      "p:last-child": { marginBottom: 0 },
    },
  },
  container: {
    display: "flex",
    flexFlow: "column nowrap",
    alignItems: "stretch",
  },
  box: {
    width: "99%",
    height: "99%",
    position: "absolute",
    top: "0",
    left: "0",
    opacity: "0.6",
    zIndex: "1000",
    margin: "2px",
    backgroundColor: "gray",
  },
  pageContainer: {
    position: "relative",
  },

  contentWrap: {
    paddingBottom: "2.5rem",
    height: "100%",
    overflow: "auto",
  },

  content: {
    height: "100%",
  },
});

const iconButtonStyles: Partial<IButtonStyles> = {
  root: {
    color: theme.palette.neutralPrimary,
    marginTop: "4px",
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};

export interface CreateDocumentPartModalProps {
  fatherPartId: string;
  documentPart: DocumentPart;
  documentInsertionPoint: DocumentInsertionPoint | undefined;
  isModalOpen: boolean;
  onHide: () => void;
  loadingManagerKey: string;
}

interface FromScratchWrapperState {
  status: "show-buttons" | "link" | "scratch" | "nontagetik";
}

const FromScratchWrapperInitialState: FromScratchWrapperState = {
  status: "show-buttons",
};

export { getPathForFileLocationExplorer };

export const CreateDocumentPartModal: React.FunctionComponent<CreateDocumentPartModalProps> = (props) => {
  const { fatherPartId, documentPart, documentInsertionPoint, isModalOpen, onHide, loadingManagerKey } = props;
  const username = useAppSelector(selectCpmUsername); // TEMP waiting for real login

  const backIcon: IIconProps = { iconName: "ChevronLeftMed" };
  const [state, setState] = React.useState<FromScratchWrapperState>(FromScratchWrapperInitialState);
  const { status } = state;
  const [currentTeachingBubble, setCurrentTeachingBubble] = useState<"sharepoint" | "tagetik">(null);

  const sharepointId = "sharepointId";
  const tagetikId = "tagetikId";

  function resetAndHide() {
    onHide();
    setState({ ...state, status: "show-buttons" });
    LoadingManager.remove(loadingManagerKey);
  }

  const scrollableContentStyle = mergeStyleSets({
    scrollableContent: mergeStyles({
      maxHeight: status === "link" || status === "nontagetik" ? "calc(100vh - 62px)" : "unset",
    }),
  });

  const getTitle = () => {
    if (status === "show-buttons") return formatMessage("cof.createdocument.newdocumentpart");
    if (status === "scratch") return formatMessage("cof.objectsource.documentpart.createfromscratch");
    if (status === "link") return formatMessage("cof.objectsource.documentpart.createfromexisting");
    return formatMessage("cof.objectsource.documentpart.createfromnontagetik");
  };

  return (
    <Modal
      titleAriaId={"titleId"}
      isOpen={isModalOpen}
      onDismiss={() => resetAndHide()}
      isBlocking={false}
      scrollableContentClassName={scrollableContentStyle.scrollableContent}
      containerClassName={contentStyles.container}
      styles={{
        main: {
          minHeight: "100%",
          minWidth: "100%",
        },
      }}
    >
      <div className={contentStyles.pageContainer}>
        <div className={contentStyles.contentWrap}>
          <div className={contentStyles.content}>
            <div className={contentStyles.header}>
              <IconButton
                styles={iconButtonStyles}
                iconProps={backIcon}
                ariaLabel="Close popup modal"
                onClick={() => resetAndHide()}
              />
              <span id={"titleId"}>{getTitle()}</span>
            </div>
            <div
              style={{
                borderBottom: "0.15rem solid",
                color: "var(--color-blue)",
                width: "100%",
              }}
            />
            {status === "show-buttons" && (
              <div
                style={{
                  width: "100vw",
                  height: "calc(100vh - 5rem)",
                  objectFit: "contain",
                  position: "fixed",
                  bottom: 0,
                }}
              >
                <img
                  src={"/assets/newDocumentPart.svg"}
                  alt="new document part logo"
                  style={{
                    minWidth: "120px",
                    width: "calc(100vw - 4rem)",
                    height: "70vh",
                    objectFit: "contain",
                    position: "fixed",
                    top: "45%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    position: "fixed",
                    bottom: 0,
                    width: "100%",
                    paddingBottom: "2rem",
                  }}
                >
                  <span
                    style={{
                      textAlign: "center",
                      color: "var(--color-blue)",
                      padding: "0 1.25rem",
                      marginBottom: "4px",
                    }}
                  >
                    {formatMessage("cof.createdocument.create.message.fromscratch")}
                  </span>
                  <PrimaryButton
                    id="from_scratch"
                    text={formatMessage("cof.createdocument.fromscratch")}
                    onClick={() => setState({ ...state, status: "scratch" })}
                    data-testid="scratch"
                    style={{ margin: "0 auto" }}
                    className={ButtonVariants.primary}
                  />
                  <span
                    style={{
                      textAlign: "center",
                      color: "var(--color-blue)",
                      padding: "0 1.25rem",
                      marginTop: "12px",
                      marginBottom: "4px",
                    }}
                  >
                    {formatMessage("cof.createdocument.create.message.fromexisting")}
                  </span>

                  <div
                    style={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                      padding: "0 8px",
                    }}
                  >
                    {currentTeachingBubble === "tagetik" && (
                      <TeachingBubble
                        calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
                        target={`#${tagetikId}`}
                        hasCloseButton={true}
                        closeButtonAriaLabel="Close"
                        onDismiss={() => setCurrentTeachingBubble(null)}
                        headline={"Tagetik"}
                      >
                        <text>{formatMessage("cof.createnewdocument.fromtagetik")}</text>
                      </TeachingBubble>
                    )}
                    <IconButton
                      id={tagetikId}
                      aria-label="Info"
                      iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
                      onClick={() => setCurrentTeachingBubble("tagetik")}
                    />
                    <PrimaryButton
                      id="from_tagetik"
                      text={formatMessage("cof.createnewpart.fromexisting")}
                      onClick={() => setState({ ...state, status: "link" })}
                      className={ButtonVariants.white}
                    />
                    <PrimaryButton
                      id="from_sharepoint"
                      text={formatMessage("cof.createnewpart.fromnontagetik")}
                      onClick={() => setState({ ...state, status: "nontagetik" })}
                      className={ButtonVariants.white}
                    />
                    <IconButton
                      id={sharepointId}
                      aria-label="Info"
                      iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
                      onClick={() => setCurrentTeachingBubble("sharepoint")}
                    />
                    {currentTeachingBubble === "sharepoint" && (
                      <TeachingBubble
                        calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
                        target={`#${sharepointId}`}
                        hasCloseButton={true}
                        closeButtonAriaLabel="Close"
                        onDismiss={() => setCurrentTeachingBubble(null)}
                        headline={"Sharepoint"}
                      >
                        <text>{formatMessage("cof.createnewdocument.fromsharepoint")}</text>
                      </TeachingBubble>
                    )}
                  </div>
                </div>
              </div>
            )}
            {status === "scratch" && (
              <div data-testid="edit-document">
                <EditDocument
                  fatherPartId={fatherPartId}
                  isEdit={false}
                  onLastBackButton={undefined}
                  onSave={(input) =>
                    createCoObjectDocumentPart(
                      fatherPartId,
                      documentPart,
                      null,
                      input.name,
                      input.description,
                      input.path,
                      documentInsertionPoint,
                      status,
                      input.advanced,
                      username,
                      input.parameters,
                      input.inheritableParameters,
                      input.language
                    ).finally(() => resetAndHide())
                  }
                  getDocumentPartDetails={async () => {
                    const inheritableMetadata = await getDocumentPartMetadataFromCustomXml();
                    const address = (await fetchDocumentPath(props.fatherPartId)) ?? "";
                    const path = getPathForFileLocationExplorer(address);
                    return {
                      documentData: {
                        description: undefined,
                        name: undefined,
                        root: false,
                        multilinked: false,
                        hasChildren: false,
                        path,
                        documentId: undefined,
                        siteId: undefined,
                        driveItemId: undefined,
                        advanced: mapMetadataToAdvanced(inheritableMetadata),
                        language: inheritableMetadata.language,
                        parameters: [],
                        inheritableParameters: inheritableMetadata?.parametersMetadata,
                      },
                    };
                  }}
                />
              </div>
            )}
            {status === "link" && (
              <div data-testid="link-existing-document">
                <LinkExistingDocument
                  onLink={(partIdToLink) =>
                    createCoObjectDocumentPart(
                      fatherPartId,
                      documentPart,
                      { partDocumentId: partIdToLink },
                      "",
                      "",
                      null,
                      documentInsertionPoint,
                      status,
                      null,
                      username,
                      null,
                      null,
                      null
                    ).finally(() => resetAndHide())
                  }
                />
              </div>
            )}
            {status === "nontagetik" && (
              <div data-testid="link-nontagetik-document">
                <LinkNonTagetikDocumentPanel
                  fatherPartId={fatherPartId}
                  onLink={(documentData, driveItemId) =>
                    createCoObjectDocumentPart(
                      fatherPartId,
                      documentPart,
                      { documentDriveItemId: driveItemId },
                      documentData.name,
                      documentData.description,
                      documentData.path,
                      documentInsertionPoint,
                      status,
                      documentData.advanced,
                      username,
                      documentData.parameters,
                      documentData.inheritableParameters,
                      documentData.language
                    ).finally(() => resetAndHide())
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
